import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import Papa from "papaparse";
import toast from "react-hot-toast";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { DateTime } from "luxon";
import "react-datepicker/dist/react-datepicker.css";

import ApiService from "../../../../services/ApiService";
import { addCommas } from "../../../../utils";
import useCampaignMemberStatuses from "./useCampaignMemberStatuses";
import {
  SALESFORCE_FILTER_RULE_DEFS,
  getSalesforceLeadFilterRuleLabel,
  getSalesforceLeadFilterRuleLabelByKey,
} from "./salesforceLeadFilterRules";

// Salesforce org timezone for Last_Sent__c display/write.
const SF_LAST_SENT_TIMEZONE = "America/New_York";
const LAST_SENT_DATE_FORMAT = "MM/dd/yyyy h:mm aa '(ET)'";

function toEtDatetimeLocalValue(date = new Date()) {
  const jsDate = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(jsDate.getTime())) return "";
  const dt = DateTime.fromJSDate(jsDate, { zone: SF_LAST_SENT_TIMEZONE });
  if (!dt.isValid) return "";
  return dt.toFormat("yyyy-MM-dd'T'HH:mm");
}

// Treat a datetime-local string as Eastern Time wall-clock, then emit UTC ISO
// for the Salesforce API so SF shows the same clock time the user picked.
function etDatetimeLocalToIso(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const dt = DateTime.fromISO(raw, { zone: SF_LAST_SENT_TIMEZONE });
  if (!dt.isValid) return "";
  return dt.toUTC().toISO();
}

// DatePicker shows browser-local Date components; we feed it a Date whose local
// wall-clock matches the ET value so the picker displays ET numbers.
function etDatetimeLocalToPickerDate(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const dt = DateTime.fromISO(raw, { zone: SF_LAST_SENT_TIMEZONE });
  if (!dt.isValid) return null;
  return new Date(dt.year, dt.month - 1, dt.day, dt.hour, dt.minute, 0, 0);
}

function pickerDateToEtDatetimeLocal(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function buildSalesforceUserOptions(users = []) {
  return (Array.isArray(users) ? users : [])
    .map((user) => {
      const id = String(user?.id || user?.Id || "").trim();
      const name = String(user?.name || user?.Name || "").trim();
      const email = String(user?.email || user?.Email || "")
        .trim()
        .toLowerCase();
      if (!id || !name || !email) return null;
      if (!email.endsWith("@landmarkventures.com")) return null;
      return {
        value: id,
        label: `${name} (${email})`,
        email,
        name,
      };
    })
    .filter(Boolean);
}

function findDefaultSalesforceUserOption(users = [], employeeEmail = "") {
  const email = String(employeeEmail || "").trim().toLowerCase();
  if (!email) return null;
  return (
    buildSalesforceUserOptions(users).find((option) => option.email === email) ||
    null
  );
}

const salesforceUserSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderColor: "#0072ff",
    borderWidth: 1,
    borderStyle: "solid",
    boxShadow: "none",
    borderRadius: "0.375rem",
    backgroundColor: "#ffffff",
    fontSize: 12,
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    cursor: "pointer",
    "&:hover": {
      borderColor: "#0072ff",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    height: 32,
    padding: "0 10px",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    fontSize: 12,
    color: "#000000",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#8ca1bf",
    fontSize: 12,
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: 12,
    color: "#000000",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 32,
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: 6,
    color: "#6c757d",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: 6,
    color: "#6c757d",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 20,
    fontSize: 12,
    borderRadius: "0.375rem",
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 12,
    backgroundColor: state.isFocused ? "#f0f4fa" : base.backgroundColor,
    color: "#000000",
  }),
};

// Multi-select chips wrap, so the fixed-height control of the single selects
// would clip them.
const excludeCampaignSelectStyles = {
  ...salesforceUserSelectStyles,
  control: (base, state) => ({
    ...salesforceUserSelectStyles.control(base, state),
    height: "auto",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "2px 6px",
  }),
  indicatorsContainer: (base) => base,
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#e8f0fe",
  }),
  multiValueLabel: (base) => ({
    ...base,
    fontSize: 11,
    color: "#000000",
  }),
};

const MAX_EXCLUDE_CAMPAIGNS = 10;

function getSalesforceIdKey(id = "") {
  return String(id || "").trim().slice(0, 15);
}

function buildExcludeCampaignOptions(campaigns = [], targetCampaignId = "") {
  const targetKey = getSalesforceIdKey(targetCampaignId);
  const seen = new Set();
  return (Array.isArray(campaigns) ? campaigns : [])
    .map((campaign) => {
      const id = String(campaign?.Id || campaign?.id || "").trim();
      const name = String(campaign?.Name || campaign?.name || "").trim();
      const key = getSalesforceIdKey(id);
      if (!id || !name || key === targetKey || seen.has(key)) return null;
      seen.add(key);
      return { value: id, label: `${name} (${id})`, name };
    })
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));
}

const CONTACT_LIST_SOURCE = Object.freeze({
  SALESFORCE: "Salesforce",
  APOLLO: "Apollo",
  SEAMLESS: "Seamless",
  ZOOMINFO: "ZoomInfo",
});
// Sentinel for the manual list-source picker: "Other" means the user supplies
// a free-text source name (and optional notes) instead of a known export type.
const MANUAL_LIST_SOURCE_OTHER = "Other";
const MANUAL_LIST_SOURCE_OPTIONS = Object.freeze([
  { value: CONTACT_LIST_SOURCE.APOLLO, label: "Apollo export" },
  { value: CONTACT_LIST_SOURCE.SEAMLESS, label: "Seamless export" },
  { value: CONTACT_LIST_SOURCE.ZOOMINFO, label: "ZoomInfo export" },
  { value: CONTACT_LIST_SOURCE.SALESFORCE, label: "Salesforce export / report" },
  { value: MANUAL_LIST_SOURCE_OTHER, label: "Other (specify)" },
]);

const EXPECTED_COLUMN_TITLES = ["Company", "First Name", "Last Name", "Job Title", "Email"];
const EMAIL_REGEX = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/;
const ROW_EMAIL_REGEX = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,7}\b/;

// Columns that carry huge amounts of data we never use for the Salesforce upload.
// Stripping these client-side (before the upload request is built) keeps the request
// body small enough to accept very large source files. In Apollo exports the
// "Technologies" and "Keywords" columns alone are ~90% of the file.
const STRIP_COLUMN_TITLES = ["Technologies", "Keywords"];
// CSV is parsed in ~10MB chunks so very large files never sit fully in browser memory.
const CSV_PARSE_CHUNK_BYTES = 10 * 1024 * 1024;
const RECONCILIATION_UPLOAD_STATUS_COLUMN = "Upload Status";
const RECONCILIATION_UPLOAD_ERROR_COLUMN = "Upload Error Reason";
const RECONCILIATION_MEMBER_STATUS_COLUMN = "Campaign Member Status";
const RECONCILIATION_MATCHED_EMAIL_COLUMN = "Matched Campaign Email";

function normalizeCsvHeader(header = "") {
  return String(header || "")
    .replace(/^\uFEFF/, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/_\d+$/, "");
}

const STRIP_COLUMN_SET = new Set(STRIP_COLUMN_TITLES.map((title) => normalizeCsvHeader(title)));

function isStrippableColumn(header = "") {
  return STRIP_COLUMN_SET.has(normalizeCsvHeader(header));
}

function detectContactListSourceFromHeaders(headers = []) {
  const normalizedHeaders = (Array.isArray(headers) ? headers : [])
    .map((header) => normalizeCsvHeader(header))
    .filter(Boolean);
  if (!normalizedHeaders.length) return "";

  const firstHeader = normalizedHeaders[0];
  if (firstHeader === "research date") return CONTACT_LIST_SOURCE.SEAMLESS;

  const hasZoomInfoIndicator = normalizedHeaders.some(
    (header) =>
      header === "zoominfo contact id" ||
      header === "zoominfo company id" ||
      header === "zoominfo contact profile url" ||
      header === "zoominfo company profile url"
  );
  if (hasZoomInfoIndicator) return CONTACT_LIST_SOURCE.ZOOMINFO;

  const hasSalesforceContactId = normalizedHeaders.some(
    (header) => header === "contact id" || header === "contact id (long)"
  );
  const hasBusinessEmail = normalizedHeaders.includes("business email");
  if (hasSalesforceContactId || hasBusinessEmail) return CONTACT_LIST_SOURCE.SALESFORCE;

  const hasApolloIndicator = normalizedHeaders.some(
    (header) =>
      header === "apollo contact id" ||
      header === "apollo account id" ||
      header === "salesforce contact id"
  );
  if (hasApolloIndicator) return CONTACT_LIST_SOURCE.APOLLO;

  return "";
}

function normalizeEmailValue(value) {
  return String(value || "").trim().toLowerCase();
}

function isValidEmail(email) {
  return EMAIL_REGEX.test(String(email || "").trim());
}

// Mirrors the server-side email column priority (Misc.mapCsvRowToSalesforceContact)
// so the reconciliation report matches what the upload pipeline actually used.
const PREFERRED_EMAIL_COLUMNS = [
  "email",
  "work email",
  "business email",
  "contact email",
  "primary email",
  "email 1",
  "email 2",
  "secondary email",
  "email address",
  "email address 1",
  "personal email",
];

function extractEmailFromRow(row = {}) {
  if (!row || typeof row !== "object") return "";
  const valueByNormalizedKey = new Map();
  for (const key of Object.keys(row)) {
    const normalizedKey = String(key || "").trim().toLowerCase();
    if (!valueByNormalizedKey.has(normalizedKey)) {
      valueByNormalizedKey.set(normalizedKey, row[key]);
    }
  }
  for (const key of PREFERRED_EMAIL_COLUMNS) {
    const value = String(valueByNormalizedKey.get(key) || "").trim();
    if (value && isValidEmail(value)) return normalizeEmailValue(value);
  }
  for (const value of Object.values(row)) {
    const raw = String(value || "").trim();
    if (!raw) continue;
    const matched = raw.match(EMAIL_REGEX);
    if (matched?.[0]) return normalizeEmailValue(matched[0]);
  }
  return "";
}

function getCampaignMemberEmail(member = {}) {
  return normalizeEmailValue(member?.Contact?.Email || member?.Lead?.Email || "");
}

function getSafeCsvFileName(baseName = "", campaignName = "") {
  const rawBase = String(baseName || "contacts-upload")
    .replace(/\.csv$/i, "")
    .trim();
  const safeCampaign = String(campaignName || "")
    .trim()
    .replace(/[^a-zA-Z0-9-_ ]+/g, "")
    .replace(/\s+/g, "-");
  const ts = new Date().toISOString().replace(/[:.]/g, "-");
  const campaignSegment = safeCampaign ? `-${safeCampaign}` : "";
  return `${rawBase}${campaignSegment}-upload-status-${ts}.csv`;
}

const CampaignContactUploader = ({
  employee,
  campaignId,
  selectedCampaign,
  refreshSelectedCampaign,
  salesforceUsers = [],
  campaigns = [],
  selectedFile = null,
  hideInitialLoadButton = false,
  onProcessingChange,
  onUploadComplete,
}) => {
  const [originalData, setOriginalData] = useState([]);
  const [csvDiagnostics, setCsvDiagnostics] = useState({
    parsedRows: 0,
    nonEmptyRows: 0,
    parseWarnings: 0,
  });
  const [detectedListSource, setDetectedListSource] = useState("");
  const [strippedColumns, setStrippedColumns] = useState([]);
  const [parsingFile, setParsingFile] = useState(false);
  const [emailKey, setEmailKey] = useState("");
  const [csvData, setCsvData] = useState([]);
  const [csvFileName, setCsvFileName] = useState("");
  const { statusOptions, defaultStatus } = useCampaignMemberStatuses({
    employee,
    salesforceCampaignId: selectedCampaign?.Id || campaignId,
    recordTypeName: selectedCampaign?.RecordType?.Name,
  });
  const [status, setStatus] = useState("");
  const [statusManuallySet, setStatusManuallySet] = useState(false);
  // Ensure the currently selected status is always shown even if it is not in
  // the campaign's status list (e.g. a manually chosen legacy value).
  const uploaderStatusOptions = useMemo(() => {
    if (status && !statusOptions.includes(status)) {
      return [status, ...statusOptions];
    }
    return statusOptions;
  }, [statusOptions, status]);
  const [skipExistingCampaignMembers, setSkipExistingCampaignMembers] = useState(true);
  // Contacts that are members (any status) of these campaigns are left out.
  const [excludeCampaignOptions, setExcludeCampaignOptions] = useState([]);
  const [fetchedCampaigns, setFetchedCampaigns] = useState([]);
  const [loadingFetchedCampaigns, setLoadingFetchedCampaigns] = useState(false);
  const fetchedCampaignsRequestedRef = useRef(false);
  const [runContactsThroughMillionVerifier, setRunContactsThroughMillionVerifier] = useState(true);
  // Sendora's Salesforce lead filters, applied server-side to resolved contacts
  // so anyone Salesforce marks as not safe/appropriate to email is never added
  // to the campaign. On by default; the per-rule breakdown is shown after upload.
  const [applySalesforceLeadFilters, setApplySalesforceLeadFilters] = useState(true);
  const [showSalesforceLeadFilterRules, setShowSalesforceLeadFilterRules] = useState(false);
  // MillionVerifier credit availability. While credits are exhausted, below the
  // server's low threshold, or cannot be confirmed, uploads that verify emails
  // are blocked so lists aren't silently dropped by failed lookups.
  const [millionVerifierCreditCheck, setMillionVerifierCreditCheck] = useState({
    status: "loading", // "loading" | "ok" | "blocked" | "error"
    credits: null,
    threshold: null,
    isLow: false,
    error: "",
  });
  const millionVerifierCheckSeqRef = useRef(0);
  const [campaignMemberComment, setCampaignMemberComment] = useState("");
  const [applyCommentToExistingCampaignMembers, setApplyCommentToExistingCampaignMembers] =
    useState(false);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [showCustomSourceInput, setShowCustomSourceInput] = useState(false);
  const [customSource, setCustomSource] = useState("");
  // When the CSV headers don't match a known export format (Apollo, Seamless,
  // ZoomInfo, Salesforce) the server cannot rank the upload's data against
  // existing contact fields and writes NO Last_Updated_Source/Date metadata,
  // even for brand-new contacts. In that case the user must say where the list
  // came from before uploading. "Other" takes a free-text source name (written
  // to Source__c) plus optional notes about the list's origin for our logs.
  const [manualListSource, setManualListSource] = useState("");
  const [listSourceNotes, setListSourceNotes] = useState("");
  // "uploader" adds new contacts/members; "modifier" only updates existing members.
  const [activeTab, setActiveTab] = useState("uploader");
  const [modifierUpdateStatus, setModifierUpdateStatus] = useState(false);
  const [modifierNewStatus, setModifierNewStatus] = useState("");
  const [modifierStatusConditionEnabled, setModifierStatusConditionEnabled] = useState(true);
  const [modifierConditionStatus, setModifierConditionStatus] = useState("");
  const [modifierUpdateComment, setModifierUpdateComment] = useState(false);
  const [modifierComment, setModifierComment] = useState("");
  const [modifierUpdateSource, setModifierUpdateSource] = useState(false);
  const [modifierSource, setModifierSource] = useState("");
  const [modifierUpdateEmailVersion, setModifierUpdateEmailVersion] = useState(false);
  const [modifierEmailVersion, setModifierEmailVersion] = useState("");
  const [modifierUpdateCampaignSentAs, setModifierUpdateCampaignSentAs] =
    useState(false);
  const salesforceUserOptions = useMemo(
    () => buildSalesforceUserOptions(salesforceUsers),
    [salesforceUsers]
  );
  const defaultSentAsOption = useMemo(
    () => findDefaultSalesforceUserOption(salesforceUsers, employee?.email),
    [salesforceUsers, employee?.email]
  );
  const [modifierCampaignSentAs, setModifierCampaignSentAs] = useState(null);
  const [modifierUpdateLastSent, setModifierUpdateLastSent] = useState(false);
  const [modifierLastSent, setModifierLastSent] = useState(() =>
    toEtDatetimeLocalValue()
  );
  // Destructive: deletes all matched members and overrides every other option.
  const [modifierDeleteMembers, setModifierDeleteMembers] = useState(false);

  useEffect(() => {
    if (!modifierCampaignSentAs && defaultSentAsOption) {
      setModifierCampaignSentAs(defaultSentAsOption);
    }
  }, [modifierCampaignSentAs, defaultSentAsOption]);
  const [modifySummary, setModifySummary] = useState(null);
  const [modifyingInSf, setModifyingInSf] = useState(false);
  const [uploadingToSf, setUploadingToSf] = useState(false);
  const [logLines, setLogLines] = useState([]);
  const [streaming, setStreaming] = useState(false);
  // Live progress for the in-flight job ({ phase, processed, total, percent, failed }).
  const [jobProgress, setJobProgress] = useState(null);
  const [uploadSummary, setUploadSummary] = useState(null);
  const [reconciliationRows, setReconciliationRows] = useState([]);
  const [reconciliationFileName, setReconciliationFileName] = useState("");
  const [buildingReconciliation, setBuildingReconciliation] = useState(false);

  const esControllerRef = useRef(null);
  const streamRetryTimeoutRef = useRef(null);
  const logRef = useRef(null);
  const csvInputRef = useRef(null);
  const prevCampaignIdRef = useRef(selectedCampaign?.Id ?? null);
  const hasCompletedSuccessfully = !!uploadSummary && !streaming;
  const uploadTargetCampaignName = useMemo(
    () => String(selectedCampaign?.Name || selectedCampaign?.name || "").trim(),
    [selectedCampaign?.Name, selectedCampaign?.name]
  );
  const uploadTargetCampaignId = String(selectedCampaign?.Id || campaignId || "").trim();
  const availableExcludeCampaignOptions = useMemo(
    () =>
      buildExcludeCampaignOptions(
        Array.isArray(campaigns) && campaigns.length ? campaigns : fetchedCampaigns,
        uploadTargetCampaignId
      ),
    [campaigns, fetchedCampaigns, uploadTargetCampaignId]
  );
  const selectedExcludeCampaignOptions = useMemo(
    () =>
      excludeCampaignOptions.filter(
        (option) => getSalesforceIdKey(option.value) !== getSalesforceIdKey(uploadTargetCampaignId)
      ),
    [excludeCampaignOptions, uploadTargetCampaignId]
  );
  const loadCampaignsForExclusion = async () => {
    if (Array.isArray(campaigns) && campaigns.length) return;
    if (fetchedCampaignsRequestedRef.current || !employee) return;
    fetchedCampaignsRequestedRef.current = true;
    setLoadingFetchedCampaigns(true);
    try {
      const api = new ApiService({ employee });
      const data = await api.get("landmark/salesforce/getCampaigns", null, null, { timeout: 0 });
      if (!Array.isArray(data)) throw new Error(data?.error || "Unexpected campaigns response");
      setFetchedCampaigns(data);
    } catch (error) {
      console.error(error);
      fetchedCampaignsRequestedRef.current = false;
      toast.error("Could not load Salesforce campaigns");
    } finally {
      setLoadingFetchedCampaigns(false);
    }
  };

  const dataHasExpectedColumns = useMemo(() => {
    return Array.isArray(originalData) && originalData.length > 0;
  }, [originalData]);

  useEffect(() => {
    if (typeof onProcessingChange === "function") {
      onProcessingChange(uploadingToSf || streaming || parsingFile);
    }
  }, [uploadingToSf, streaming, parsingFile, onProcessingChange]);

  useEffect(() => {
    if (hasCompletedSuccessfully && typeof onUploadComplete === "function") {
      onUploadComplete();
    }
  }, [hasCompletedSuccessfully, onUploadComplete]);

  useEffect(() => {
    const newId = selectedCampaign?.Id ?? null;
    const prevId = prevCampaignIdRef.current;
    const changed = prevId !== newId;
    const nextManual = changed ? false : statusManuallySet;
    if (changed) {
      prevCampaignIdRef.current = newId;
      setStatusManuallySet(false);
    }
    // Default to the campaign's own default ("not yet sent") status, which the
    // hook resolves from the campaign's CampaignMemberStatus list (e.g. BD
    // campaigns default to "To be sent", Events to "Unsent").
    if (!nextManual && defaultStatus) {
      setStatus(defaultStatus);
    }
  }, [selectedCampaign?.Id, defaultStatus, statusManuallySet]);

  const checkMillionVerifierCredits = async () => {
    const seq = ++millionVerifierCheckSeqRef.current;
    setMillionVerifierCreditCheck((prev) => ({ ...prev, status: "loading", error: "" }));
    try {
      const api = new ApiService({ employee });
      const result = await api.get("landmark/salesforce/millionVerifierCredits/check");
      if (seq !== millionVerifierCheckSeqRef.current) return;
      if (!result || result.ok !== true || result.error) {
        setMillionVerifierCreditCheck({
          status: "error",
          credits: null,
          threshold: null,
          isLow: false,
          error: String(result?.error || "No response from credits check").trim(),
        });
        return;
      }
      const credits = Number(result.credits);
      const threshold = Number(result.threshold);
      const hasNoCredits = !Number.isFinite(credits) || credits <= 0;
      const isLow = !!result.isLow || hasNoCredits;
      setMillionVerifierCreditCheck({
        status: isLow ? "blocked" : "ok",
        credits: Number.isFinite(credits) ? credits : 0,
        threshold: Number.isFinite(threshold) ? threshold : null,
        isLow,
        error: "",
      });
    } catch (error) {
      if (seq !== millionVerifierCheckSeqRef.current) return;
      console.error(error);
      setMillionVerifierCreditCheck({
        status: "error",
        credits: null,
        threshold: null,
        isLow: false,
        error: String(error?.message || error || "Credits check failed").trim(),
      });
    }
  };

  // Check on mount and again whenever a new file is loaded so the state shown
  // next to the checkbox is fresh at the moment the user is about to upload.
  useEffect(() => {
    checkMillionVerifierCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee?.email, csvFileName]);

  const millionVerifierCreditsUnavailable =
    millionVerifierCreditCheck.status === "blocked" ||
    millionVerifierCreditCheck.status === "error";
  // Loading counts as blocked so an upload can't slip through before the
  // check resolves.
  const uploadBlockedByMillionVerifier =
    runContactsThroughMillionVerifier && millionVerifierCreditCheck.status !== "ok";

  const millionVerifierCreditsErrorMessage = useMemo(() => {
    if (!millionVerifierCreditsUnavailable) return "";
    const { status, credits, threshold, error } = millionVerifierCreditCheck;
    let lead = "";
    if (status === "error") {
      lead = `Could not confirm MillionVerifier credits${error ? ` (${error})` : ""}.`;
    } else if (!credits || credits <= 0) {
      lead = "MillionVerifier has no credits left.";
    } else {
      lead = `MillionVerifier credits are running low (${addCommas(credits)} remaining${
        threshold ? `, minimum ${addCommas(threshold)}` : ""
      }).`;
    }
    const action = runContactsThroughMillionVerifier
      ? ' Uploading this list is disabled while "Run contacts through MillionVerifier" is ticked. Untick it to upload without email verification.'
      : " Uploads with email verification are disabled until credits are topped up.";
    return `${lead}${action} If this problem persists, contact mjones@landmarkventures.com.`;
  }, [millionVerifierCreditsUnavailable, millionVerifierCreditCheck, runContactsThroughMillionVerifier]);

  // List source gating. Auto-detection from the CSV headers wins; otherwise the
  // user's manual pick is used. A manual pick of a known source (Apollo,
  // Seamless, ZoomInfo, Salesforce) is sent as the list source so the server
  // can tier-rank and stamp it. "Other" sends the typed name as the custom
  // source instead; the server falls back to that for stamping.
  const listSourceNeedsSelection = dataHasExpectedColumns && !detectedListSource;
  const manualListSourceIsOther = manualListSource === MANUAL_LIST_SOURCE_OTHER;
  const trimmedCustomSource = String(customSource || "").trim();
  const effectiveListSource =
    detectedListSource ||
    (manualListSource && !manualListSourceIsOther ? manualListSource : "");
  const uploadBlockedByMissingListSource =
    listSourceNeedsSelection &&
    (!manualListSource || (manualListSourceIsOther && !trimmedCustomSource));
  const missingListSourceMessage = !listSourceNeedsSelection
    ? ""
    : !manualListSource
    ? "The list source could not be detected from this file's columns. Pick where the list came from so Salesforce contact metadata (Last Updated Source/Date) can be recorded."
    : manualListSourceIsOther && !trimmedCustomSource
    ? "Enter a name for this source (it is written to the Source field on campaign members and new contacts)."
    : "";

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logLines]);

  useEffect(() => {
    return () => {
      if (streamRetryTimeoutRef.current) clearTimeout(streamRetryTimeoutRef.current);
      esControllerRef.current?.close?.();
    };
  }, []);

  const appendLog = (line) => setLogLines((prev) => [...prev, line]);

  const formatLogLine = (payload) => {
    if (!payload) return "";
    const ts = new Date(payload.ts || Date.now()).toLocaleTimeString();
    if (typeof payload === "string") return `[${ts}] ${payload}`;
    if (payload.msg) return `[${ts}] ${payload.msg}`;
    return `[${ts}] ${typeof payload === "object" ? JSON.stringify(payload) : String(payload)}`;
  };

  const extractAsyncJobId = (response) => {
    if (!response) return null;
    let payload = response;
    if (typeof response === "string") {
      try {
        payload = JSON.parse(response);
      } catch {
        return null;
      }
    }
    const candidates = [
      payload?.jobId,
      payload?.jobID,
      payload?.taskId,
      payload?.taskID,
      payload?.id,
      payload?.data?.jobId,
      payload?.data?.jobID,
      payload?.data?.taskId,
      payload?.data?.taskID,
      payload?.data?.id,
    ];
    return candidates.find((value) => typeof value === "string" && value.trim()) || null;
  };

  const startStream = (api, jobId, retryAttempt = 0, { kind = "upload" } = {}) => {
    if (!jobId) return;
    const MAX_UNKNOWN_JOB_RETRIES = 5;
    esControllerRef.current?.close?.();
    if (streamRetryTimeoutRef.current) clearTimeout(streamRetryTimeoutRef.current);

    setStreaming(true);
    if (retryAttempt === 0) appendLog(`Opened stream for job ${jobId}`);
    else appendLog(`Retrying stream for job ${jobId} (${retryAttempt}/${MAX_UNKNOWN_JOB_RETRIES})`);

    const controller = api.openJobStream(jobId, {
      onOpen: (_ev, meta) => appendLog(`[${new Date().toLocaleTimeString()}] SSE opened -> ${meta?.url ?? ""}`),
      onLog: (payloadOrString) => appendLog(formatLogLine(payloadOrString)),
      events: {
        progress: (payload) => {
          if (payload && typeof payload === "object") setJobProgress(payload);
        },
      },
      onDone: async (payload) => {
        setJobProgress(null);
        if (kind === "modify") {
          setModifySummary(payload?.summary || null);
          appendLog(formatLogLine({ msg: "Modify complete", ts: payload?.ts || Date.now() }));
          setStreaming(false);
          toast.success("Campaign members modified");
          esControllerRef.current = null;
          if (typeof refreshSelectedCampaign === "function") {
            refreshSelectedCampaign({ silent: true, showContactsRefresh: true });
          }
          return;
        }
        setUploadSummary(payload?.uploadSummary || null);
        appendLog(formatLogLine({ msg: "Job complete", ts: payload?.ts || Date.now() }));
        setStreaming(false);
        await buildUploadReconciliationCsv(api, payload?.uploadSummary || null);
        toast.success("Contacts uploaded to Salesforce Campaign");
        esControllerRef.current = null;
        if (typeof refreshSelectedCampaign === "function") {
          refreshSelectedCampaign({ silent: true, showContactsRefresh: true });
        }
      },
      onError: (payload) => {
        const errorMsg = String(payload?.error || "").trim().toLowerCase();
        const isUnknownJobIdError =
          errorMsg === "unknown jobid" || errorMsg.includes("unknown jobid");
        if (isUnknownJobIdError && retryAttempt < MAX_UNKNOWN_JOB_RETRIES) {
          const nextAttempt = retryAttempt + 1;
          const retryDelayMs = Math.min(1500 * nextAttempt, 5000);
          appendLog(
            formatLogLine({
              msg: `Stream could not find job yet. Retrying in ${Math.round(retryDelayMs / 1000)}s...`,
              ts: Date.now(),
            })
          );
          esControllerRef.current?.close?.();
          esControllerRef.current = null;
          streamRetryTimeoutRef.current = setTimeout(
            () => startStream(api, jobId, nextAttempt, { kind }),
            retryDelayMs
          );
          return;
        }
        if (payload?.error) appendLog(formatLogLine({ msg: `Error: ${payload.error}` }));
        appendLog(formatLogLine({ msg: "Stream closed (error or server finished).", ts: Date.now() }));
        esControllerRef.current?.close?.();
        setStreaming(false);
        setJobProgress(null);
        esControllerRef.current = null;
      },
    });

    esControllerRef.current = controller;
  };

  const buildUploadReconciliationCsv = async (api, summary = null) => {
    if (!campaignId || !Array.isArray(originalData) || !originalData.length) {
      setReconciliationRows([]);
      setReconciliationFileName("");
      return;
    }
    setBuildingReconciliation(true);
    appendLog("Building upload reconciliation report...");
    try {
      const withMembers = true;
      const campaign = await api.get(
        `landmark/salesforce/getCampaign/${encodeURIComponent(campaignId)}/${withMembers}`,
        null,
        null,
        { timeout: 0 }
      );
      const members = Array.isArray(campaign?.members) ? campaign.members : [];
      const memberEmails = new Set();
      const memberByEmail = new Map();
      const memberByContactId = new Map();
      members.forEach((member) => {
        const contactId = String(member?.ContactId || "").trim();
        if (contactId && !memberByContactId.has(contactId)) {
          memberByContactId.set(contactId, member);
        }
        const email = getCampaignMemberEmail(member);
        if (!email) return;
        memberEmails.add(email);
        if (!memberByEmail.has(email)) memberByEmail.set(email, member);
      });

      // Rows resolved to an existing Salesforce contact whose email differs from
      // the CSV email (matched by account+name, LinkedIn, or duplicate rule).
      // These members can't be found by email, so match them by ContactId.
      const resolvedContactIdByCsvEmail = new Map();
      (Array.isArray(summary?.resolvedEmailContactPairs)
        ? summary.resolvedEmailContactPairs
        : []
      ).forEach((pair) => {
        const csvEmail = normalizeEmailValue(pair?.email);
        const contactId = String(pair?.contactId || "").trim();
        if (csvEmail && contactId && !resolvedContactIdByCsvEmail.has(csvEmail)) {
          resolvedContactIdByCsvEmail.set(csvEmail, contactId);
        }
      });

      const failedCreateContacts = Array.isArray(summary?.failedCreateContacts)
        ? summary.failedCreateContacts
        : [];
      const unresolvedContacts = Array.isArray(summary?.unresolvedContacts)
        ? summary.unresolvedContacts
        : [];
      const millionVerifierBlockedEmails = Array.isArray(summary?.millionVerifierBlockedEmails)
        ? summary.millionVerifierBlockedEmails
        : [];
      const millionVerifierBlockedEmailSet = new Set(
        millionVerifierBlockedEmails.map((email) => normalizeEmailValue(email))
      );
      // The server caps the blocked-email list it sends back. When the cap was
      // hit, rows missing from the campaign most likely were filtered by
      // MillionVerifier too, so say so instead of a generic "not found".
      const blockedListTruncated = !!summary?.truncated?.millionVerifierBlockedEmails;
      const millionVerifierFailedRequests = Number(
        summary?.metrics?.millionVerifierFailedRequests || 0
      );
      // Contacts removed by the Salesforce lead filters, keyed by every CSV
      // email that resolved to them, with the human-readable rule labels.
      const leadFilterCampaignName = String(
        summary?.salesforceLeadFilters?.campaignName || uploadTargetCampaignName || ""
      );
      const leadFilterReasonByEmail = new Map();
      (Array.isArray(summary?.salesforceLeadFilters?.filteredContacts)
        ? summary.salesforceLeadFilters.filteredContacts
        : []
      ).forEach((entry) => {
        const ruleLabels = (Array.isArray(entry?.failedRules) ? entry.failedRules : [])
          .map((key) => getSalesforceLeadFilterRuleLabelByKey(key, leadFilterCampaignName))
          .filter(Boolean);
        const reason = `Filtered out by Salesforce lead filters${
          ruleLabels.length ? `: ${ruleLabels.join("; ")}` : ""
        }`;
        const emails = [
          ...(Array.isArray(entry?.csvEmails) ? entry.csvEmails : []),
          entry?.email,
        ]
          .map((email) => normalizeEmailValue(email))
          .filter(Boolean);
        emails.forEach((email) => {
          if (!leadFilterReasonByEmail.has(email)) leadFilterReasonByEmail.set(email, reason);
        });
      });
      const leadFilterListTruncated = !!summary?.truncated?.salesforceLeadFilteredContacts;
      // Contacts left out because they are members of an excluded campaign.
      const excludedCampaignNameById = new Map(
        (Array.isArray(summary?.excludedCampaignMembers?.campaigns)
          ? summary.excludedCampaignMembers.campaigns
          : []
        ).map((campaign) => [
          getSalesforceIdKey(campaign?.id),
          String(campaign?.name || campaign?.id || "").trim(),
        ])
      );
      const excludedReasonByEmail = new Map();
      (Array.isArray(summary?.excludedCampaignMembers?.filteredContacts)
        ? summary.excludedCampaignMembers.filteredContacts
        : []
      ).forEach((entry) => {
        const campaignNames = (Array.isArray(entry?.campaignIds) ? entry.campaignIds : [])
          .map((id) => excludedCampaignNameById.get(getSalesforceIdKey(id)) || String(id || ""))
          .filter(Boolean);
        const reason = entry?.lookupFailed
          ? "Not uploaded: could not check this contact against the excluded campaign(s) (Salesforce lookup failed)"
          : `Already a member of excluded campaign${campaignNames.length > 1 ? "s" : ""}${
              campaignNames.length ? `: ${campaignNames.join("; ")}` : ""
            }`;
        [...(Array.isArray(entry?.csvEmails) ? entry.csvEmails : []), entry?.email]
          .map((email) => normalizeEmailValue(email))
          .filter(Boolean)
          .forEach((email) => {
            if (!excludedReasonByEmail.has(email)) excludedReasonByEmail.set(email, reason);
          });
      });
      const excludedListTruncated = !!summary?.truncated?.excludedCampaignMembers;
      const errorByEmail = new Map();
      failedCreateContacts.forEach((contact) => {
        const email = normalizeEmailValue(contact?.email);
        if (!email) return;
        const message = String(contact?.error || "").trim();
        if (message) errorByEmail.set(email, message);
      });
      unresolvedContacts.forEach((contact) => {
        const email = normalizeEmailValue(contact?.email);
        if (!email || errorByEmail.has(email)) return;
        const unresolvedReason = String(contact?.error || "").trim();
        if (unresolvedReason) errorByEmail.set(email, unresolvedReason);
      });

      const rows = originalData.map((row) => {
        const normalizedEmail = extractEmailFromRow(row);
        const blockedByMillionVerifier =
          normalizedEmail && millionVerifierBlockedEmailSet.has(normalizedEmail);
        const leadFilterReason = normalizedEmail
          ? leadFilterReasonByEmail.get(normalizedEmail) || ""
          : "";
        const excludedReason = normalizedEmail
          ? excludedReasonByEmail.get(normalizedEmail) || ""
          : "";
        const memberMatchedByEmail = normalizedEmail
          ? memberByEmail.get(normalizedEmail)
          : null;
        // Fall back to the resolved ContactId when the Salesforce contact's email
        // differs from the CSV email (e.g. existing contact matched by name).
        const resolvedContactId = normalizedEmail
          ? resolvedContactIdByCsvEmail.get(normalizedEmail)
          : null;
        const memberMatchedByContactId =
          !memberMatchedByEmail && resolvedContactId
            ? memberByContactId.get(resolvedContactId)
            : null;
        const matchedMember = memberMatchedByEmail || memberMatchedByContactId || null;
        // A lead-filtered contact may already be a campaign member from an
        // earlier upload (it would have been skipped as existing before the
        // filters ran), so a member match alone does not mean this row was
        // uploaded when the filters flagged it.
        const uploaded =
          !blockedByMillionVerifier &&
          !leadFilterReason &&
          !excludedReason &&
          normalizedEmail &&
          !!matchedMember;
        const matchedMemberEmail = matchedMember ? getCampaignMemberEmail(matchedMember) : "";

        let errorReason = "";
        if (!uploaded) {
          if (!normalizedEmail) errorReason = "Missing email";
          else if (excludedReason) errorReason = excludedReason;
          else if (blockedByMillionVerifier)
            errorReason = "Filtered out by MillionVerifier (likely bad/bounce email)";
          else if (leadFilterReason) errorReason = leadFilterReason;
          else if (!isValidEmail(normalizedEmail)) errorReason = "Bad email format";
          else if (errorByEmail.has(normalizedEmail)) errorReason = errorByEmail.get(normalizedEmail);
          else if (excludedListTruncated)
            errorReason =
              "Not found in Salesforce campaign after upload - most likely a member of an excluded campaign (excluded-contact list was truncated by the server)";
          else if (leadFilterListTruncated)
            errorReason =
              "Not found in Salesforce campaign after upload - most likely filtered out by the Salesforce lead filters (filtered-contact list was truncated by the server)";
          else if (blockedListTruncated)
            errorReason =
              "Not found in Salesforce campaign after upload - most likely filtered out by MillionVerifier (blocked-email list was truncated by the server)";
          else if (millionVerifierFailedRequests > 0)
            errorReason = `Not found in Salesforce campaign after upload - ${addCommas(
              millionVerifierFailedRequests
            )} MillionVerifier lookup(s) failed during this upload (check MillionVerifier credits)`;
          else
            errorReason =
              "Contact not found in Salesforce campaign after upload (possibly filtered, duplicate, or Salesforce validation issue)";
        } else if (memberMatchedByContactId && matchedMemberEmail !== normalizedEmail) {
          errorReason = `Uploaded as existing Salesforce contact with a different email (${
            matchedMemberEmail || "no email on contact"
          })`;
        }

        return {
          ...row,
          [RECONCILIATION_MATCHED_EMAIL_COLUMN]:
            (uploaded ? matchedMemberEmail : normalizedEmail) || normalizedEmail || "",
          [RECONCILIATION_UPLOAD_STATUS_COLUMN]: uploaded ? "Uploaded" : "Not uploaded",
          [RECONCILIATION_MEMBER_STATUS_COLUMN]: uploaded ? String(matchedMember?.Status || "") : "",
          [RECONCILIATION_UPLOAD_ERROR_COLUMN]: errorReason,
        };
      });

      setReconciliationRows(rows);
      setReconciliationFileName(getSafeCsvFileName(csvFileName, uploadTargetCampaignName));
      appendLog(`Reconciliation report ready (${rows.length} row${rows.length === 1 ? "" : "s"}).`);
    } catch (error) {
      console.error(error);
      setReconciliationRows([]);
      setReconciliationFileName("");
      appendLog(`Failed to build reconciliation report: ${error.message || String(error)}`);
      toast.error("Failed to build upload reconciliation report");
    } finally {
      setBuildingReconciliation(false);
    }
  };

  const downloadReconciliationCsv = () => {
    if (!Array.isArray(reconciliationRows) || !reconciliationRows.length) {
      toast.error("No reconciliation report available yet");
      return;
    }
    try {
      const csv = Papa.unparse(reconciliationRows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      if (link.download === undefined) {
        toast.error("File download not supported in this browser");
        return;
      }
      const objectUrl = URL.createObjectURL(blob);
      link.setAttribute("href", objectUrl);
      link.setAttribute(
        "download",
        reconciliationFileName || getSafeCsvFileName(csvFileName, uploadTargetCampaignName)
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
      toast.success("Upload reconciliation CSV downloaded");
    } catch (error) {
      console.error(error);
      toast.error("Failed to download upload reconciliation CSV");
    }
  };

  const parseSelectedFile = (file) => {
    if (!file) return;
    setCsvFileName(file.name);
    setParsingFile(true);
    setOriginalData([]);
    setCsvData([]);
    setDetectedListSource("");
    setStrippedColumns([]);
    setCsvDiagnostics({ parsedRows: 0, nonEmptyRows: 0, parseWarnings: 0 });

    // Accumulators that live across streamed chunks so we never hold the whole
    // (huge) source file in memory at once. Heavy/unused columns are dropped as
    // each chunk arrives, so only the trimmed-down rows are kept.
    const rows = [];
    const emailsSet = new Set();
    const strippedSet = new Set();
    let nonEmptyRows = 0;
    let parseWarnings = 0;
    let headers = [];
    let detectedEmailKey = "";

    Papa.parse(file, {
      header: true,
      skipEmptyLines: "greedy",
      worker: true,
      chunkSize: CSV_PARSE_CHUNK_BYTES,
      chunk: (results) => {
        if (!headers.length && Array.isArray(results?.meta?.fields)) {
          headers = results.meta.fields;
        }
        if (Array.isArray(results?.errors)) parseWarnings += results.errors.length;
        const chunkRows = Array.isArray(results?.data) ? results.data : [];
        for (const rawRow of chunkRows) {
          if (!rawRow || typeof rawRow !== "object") continue;
          const row = {};
          let rowHasValue = false;
          for (const key of Object.keys(rawRow)) {
            const value = rawRow[key];
            const valueStr = String(value ?? "").trim();
            if (isStrippableColumn(key)) {
              if (valueStr !== "") strippedSet.add(key);
              continue;
            }
            row[key] = value;
            if (valueStr !== "") rowHasValue = true;
            if (typeof value === "string" && ROW_EMAIL_REGEX.test(value)) {
              emailsSet.add(value.toLowerCase().trim());
              if (!detectedEmailKey) detectedEmailKey = key;
            }
          }
          if (rowHasValue) nonEmptyRows += 1;
          rows.push(row);
        }
      },
      complete: () => {
        const listSource = detectContactListSourceFromHeaders(
          headers.length ? headers : Object.keys(rows[0] || {})
        );
        if (detectedEmailKey && detectedEmailKey !== emailKey) setEmailKey(detectedEmailKey);
        setOriginalData(rows);
        setDetectedListSource(listSource);
        // A new file may be a different source; make the user choose again.
        setManualListSource("");
        setListSourceNotes("");
        setStrippedColumns(Array.from(strippedSet));
        setCsvDiagnostics({ parsedRows: rows.length, nonEmptyRows, parseWarnings });
        setCsvData(Array.from(emailsSet));
        setParsingFile(false);
      },
      error: (error) => {
        console.error(error);
        setParsingFile(false);
        toast.error(`Failed to parse CSV: ${error?.message || String(error)}`);
      },
    });
  };

  useEffect(() => {
    if (!selectedFile) return;
    parseSelectedFile(selectedFile);
  }, [selectedFile]);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    parseSelectedFile(file);
  };

  const handleRemoveFile = () => {
    if (csvInputRef.current) csvInputRef.current.value = "";
    setCsvFileName("");
    setCsvData([]);
    setOriginalData([]);
    setDetectedListSource("");
    setStrippedColumns([]);
    setParsingFile(false);
    setCsvDiagnostics({ parsedRows: 0, nonEmptyRows: 0, parseWarnings: 0 });
    setLogLines([]);
    setUploadSummary(null);
    setReconciliationRows([]);
    setReconciliationFileName("");
    setBuildingReconciliation(false);
    setShowCommentInput(false);
    setShowCustomSourceInput(false);
    setCustomSource("");
    setManualListSource("");
    setListSourceNotes("");
    setApplySalesforceLeadFilters(true);
    setShowSalesforceLeadFilterRules(false);
    setModifierUpdateStatus(false);
    setModifierNewStatus("");
    setModifierStatusConditionEnabled(true);
    setModifierConditionStatus("");
    setModifierUpdateComment(false);
    setModifierComment("");
    setModifierUpdateSource(false);
    setModifierSource("");
    setModifierUpdateEmailVersion(false);
    setModifierEmailVersion("");
    setModifierUpdateCampaignSentAs(false);
    setModifierCampaignSentAs(defaultSentAsOption || null);
    setModifierUpdateLastSent(false);
    setModifierLastSent(toEtDatetimeLocalValue());
    setModifierDeleteMembers(false);
    setModifySummary(null);
    esControllerRef.current?.close?.();
    esControllerRef.current = null;
    setStreaming(false);
    setJobProgress(null);
  };

  const uploadContactsToSalesforce = async () => {
    if (!campaignId || !status) {
      toast.error("Missing campaignId or status");
      return;
    }
    if (uploadBlockedByMillionVerifier) {
      toast.error(
        millionVerifierCreditCheck.status === "loading"
          ? "Still checking MillionVerifier credits, please wait a moment"
          : 'MillionVerifier credits are unavailable. Untick "Run contacts through MillionVerifier" to upload without verification.'
      );
      return;
    }
    if (uploadBlockedByMissingListSource) {
      toast.error(
        missingListSourceMessage ||
          "Pick where this list came from before uploading"
      );
      return;
    }
    try {
      setUploadingToSf(true);
      setLogLines([]);
      setJobProgress(null);
      setUploadSummary(null);
      setReconciliationRows([]);
      setReconciliationFileName("");
      const api = new ApiService({ employee });
      const body = {
        contactsData: originalData,
        campaignId,
        campaignName: uploadTargetCampaignName,
        uploadedFileName: csvFileName,
        status,
        skipExistingCampaignMembers,
        excludeCampaignIds: selectedExcludeCampaignOptions.map((option) => option.value),
        runContactsThroughMillionVerifier,
        // Server-side Salesforce lead filters (same rules as Sendora). Contacts
        // failing any rule are not attached to the campaign.
        applySalesforceLeadFilters,
        campaignMemberComment: String(campaignMemberComment || "").trim(),
        applyCommentToExistingCampaignMembers,
        // Auto-detected from the CSV headers, or the user's manual pick of a
        // known source when detection failed. Drives the server's source-tier
        // comparison and the Last_Updated_Source/Date stamps on contacts.
        listSource: effectiveListSource,
        // How the list source was arrived at (for the server job log):
        // "detected" | "manual" | "manual-other" | "none".
        listSourceOrigin: detectedListSource
          ? "detected"
          : manualListSourceIsOther
          ? "manual-other"
          : manualListSource
          ? "manual"
          : "none",
        // Optional free text about where the list actually came from (only
        // asked for when the source is "Other"). Logged server-side.
        listSourceNotes: manualListSourceIsOther
          ? String(listSourceNotes || "").trim()
          : "",
        // When set, the custom source overrides the auto-detected list source for
        // both campaign members and any newly created contacts (server-side).
        // For "Other" this is the required source name.
        customSource: trimmedCustomSource,
      };
      appendLog("Starting upload...");
      let uploadEndpoint = "landmark/salesforce/upsertContactsToCampaignOptimized";
      let result = await api.post(uploadEndpoint, body, null, { timeout: 0 });
      const maybeError = String(result?.error || "").toLowerCase();
      const endpointMissing =
        maybeError.includes("cannot post") || maybeError.includes("404") || maybeError.includes("not found");
      if (endpointMissing) {
        uploadEndpoint = "landmark/salesforce/upsertContactsToCampaign";
        appendLog("Optimized upload endpoint unavailable, falling back to original...");
        result = await api.post(uploadEndpoint, body, null, { timeout: 0 });
      }
      const jobId = extractAsyncJobId(result);
      if (!jobId) {
        if (result?.error) {
          appendLog(`Server rejected upload: ${result.error}`);
          toast.error(result.error);
        } else {
          appendLog(`Did not receive a jobId from server. Payload: ${JSON.stringify(result ?? {})}`);
          toast.error("Failed to start job (no jobId).");
        }
        return;
      }
      appendLog(`Upload job started via ${uploadEndpoint}`);
      startStream(api, jobId);
    } catch (error) {
      console.error(error);
      appendLog(`Client error: ${error.message || String(error)}`);
      toast.error("Failed to upload contacts to Salesforce");
    } finally {
      setUploadingToSf(false);
    }
  };

  const handleUploadStatusChange = (e) => {
    const nextStatus = e.target.value;
    if (nextStatus) setStatusManuallySet(true);
    setStatus(nextStatus);
  };

  const effectiveModifierNewStatus = modifierNewStatus || defaultStatus || "";
  const effectiveModifierConditionStatus = modifierConditionStatus || defaultStatus || "";
  const modifierHasSelection =
    modifierDeleteMembers ||
    (modifierUpdateStatus && !!effectiveModifierNewStatus) ||
    modifierUpdateComment ||
    modifierUpdateSource ||
    modifierUpdateEmailVersion ||
    (modifierUpdateCampaignSentAs && !!modifierCampaignSentAs?.value) ||
    (modifierUpdateLastSent && !!modifierLastSent);

  const modifyContactsInSalesforce = async () => {
    if (!campaignId) {
      toast.error("Missing campaignId");
      return;
    }
    if (!modifierHasSelection) {
      toast.error(
        "Enable at least one field to update (status, comment, source, email version, campaign sent as, or last sent)"
      );
      return;
    }
    if (modifierUpdateCampaignSentAs && !modifierCampaignSentAs?.value) {
      toast.error("Select a Campaign Sent As user");
      return;
    }
    const modifierLastSentIso = modifierUpdateLastSent
      ? etDatetimeLocalToIso(modifierLastSent)
      : "";
    if (modifierUpdateLastSent && !modifierLastSentIso) {
      toast.error("Select a valid Last Sent datetime (Eastern Time)");
      return;
    }
    if (modifierDeleteMembers) {
      const confirmed = window.confirm(
        "This will permanently delete ALL matching campaign members from this campaign. This cannot be undone. Continue?"
      );
      if (!confirmed) return;
    }
    try {
      setModifyingInSf(true);
      setLogLines([]);
      setJobProgress(null);
      setModifySummary(null);
      setUploadSummary(null);
      const api = new ApiService({ employee });
      const body = {
        contactsData: originalData,
        campaignId,
        campaignName: uploadTargetCampaignName,
        uploadedFileName: csvFileName,
        updateStatus: modifierUpdateStatus,
        newStatus: modifierUpdateStatus ? effectiveModifierNewStatus : "",
        onlyWhenCurrentStatus:
          modifierUpdateStatus && modifierStatusConditionEnabled
            ? effectiveModifierConditionStatus
            : "",
        updateComment: modifierUpdateComment,
        comment: String(modifierComment || "").trim(),
        updateSource: modifierUpdateSource,
        source: String(modifierSource || "").trim(),
        updateEmailVersion: modifierUpdateEmailVersion,
        emailVersion: String(modifierEmailVersion || "").trim(),
        updateCampaignSentAs: modifierUpdateCampaignSentAs,
        campaignSentAs: modifierUpdateCampaignSentAs
          ? String(modifierCampaignSentAs?.value || "").trim()
          : "",
        updateLastSent: modifierUpdateLastSent,
        lastSent: modifierLastSentIso,
        deleteMembers: modifierDeleteMembers,
      };
      appendLog(
        modifierDeleteMembers
          ? "Starting campaign member deletion..."
          : "Starting campaign member modification..."
      );
      const endpoint = "landmark/salesforce/modifyCampaignMembersFromList";
      const result = await api.post(endpoint, body, null, { timeout: 0 });
      const jobId = extractAsyncJobId(result);
      if (!jobId) {
        if (result?.error) {
          appendLog(`Server rejected modify: ${result.error}`);
          toast.error(result.error);
        } else {
          appendLog(`Did not receive a jobId from server. Payload: ${JSON.stringify(result ?? {})}`);
          toast.error("Failed to start job (no jobId).");
        }
        return;
      }
      appendLog("Modify job started");
      startStream(api, jobId, 0, { kind: "modify" });
    } catch (error) {
      console.error(error);
      appendLog(`Client error: ${error.message || String(error)}`);
      toast.error("Failed to modify campaign members");
    } finally {
      setModifyingInSf(false);
    }
  };

  return (
    <div className="campaign-dash-csv-upload-wrapper">
      <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
        <div className="row">
          <div className="dedupe-form-wrapper">
            <div className="form-section">
              <div className="campaign-uploader-tabs">
                <button
                  type="button"
                  className={`campaign-uploader-tab ${
                    activeTab === "uploader" ? "is-active" : ""
                  }`}
                  onClick={() => setActiveTab("uploader")}
                >
                  Contact Uploader
                </button>
                <button
                  type="button"
                  className={`campaign-uploader-tab ${
                    activeTab === "modifier" ? "is-active" : ""
                  }`}
                  onClick={() => setActiveTab("modifier")}
                >
                  Contact Modifier
                </button>
              </div>
              <div className="campaign-dash-csv-upload-main">
                <div className="events-owners-subheading">
                  {activeTab === "modifier"
                    ? uploadTargetCampaignName
                      ? `Modify existing members in "${uploadTargetCampaignName}"`
                      : "Modify existing campaign members"
                    : uploadTargetCampaignName
                    ? `Upload contacts CSV to "${uploadTargetCampaignName}"`
                    : "Upload contacts CSV to Salesforce Campaign"}
                </div>
                {(!csvData || csvData.length === 0) && !hideInitialLoadButton && (
                  <button
                    className="upload-csv-btn btn btn-sm btn-primary"
                    onClick={() => csvInputRef.current?.click()}
                    disabled={parsingFile}
                  >
                    {parsingFile ? "Parsing CSV..." : "Load contacts from CSV"}
                    <i
                      className={`event-owner-add fa ml-05 ${
                        parsingFile ? "fa-spinner fa-spin" : "fa-upload"
                      }`}
                    />
                  </button>
                )}
                {parsingFile && (!csvData || csvData.length === 0) ? (
                  <div className="file-name sm mt-1">
                    Parsing large CSV and stripping unused columns ({STRIP_COLUMN_TITLES.join(", ")})...
                  </div>
                ) : null}
                <input
                  type="file"
                  accept=".csv"
                  id="csv-file-input"
                  ref={csvInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileUpload}
                />

                {csvData && csvData.length > 0 ? (
                  <div className="mt-2">
                    <div className="file-name file-name-with-action">
                      <span className="file-name__text">{csvFileName}</span>
                      <button
                        type="button"
                        className="btn btn-xs btn-danger remove-file-btn"
                        onClick={handleRemoveFile}
                        title="Remove file"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="file-name">Contacts found: {addCommas(originalData.length)}</div>
                    <div className="file-name sm">
                      {`Parsed rows: ${addCommas(csvDiagnostics.parsedRows)} | Non-empty rows: ${addCommas(csvDiagnostics.nonEmptyRows)} | Parse warnings: ${addCommas(csvDiagnostics.parseWarnings)}`}
                    </div>
                    <div className="file-name sm">
                      {detectedListSource
                        ? `Detected list source: ${detectedListSource}`
                        : activeTab === "uploader" && manualListSource
                        ? `List source (set manually): ${
                            manualListSourceIsOther
                              ? trimmedCustomSource || "Other"
                              : manualListSource
                          }`
                        : "Detected list source: Unknown"}
                    </div>
                    {activeTab === "uploader" && String(customSource || "").trim() ? (
                      <div className="file-name sm">
                        {`Custom source override: ${String(customSource).trim()}`}
                      </div>
                    ) : null}
                    {strippedColumns.length > 0 ? (
                      <div className="file-name sm">
                        {`Stripped heavy unused columns: ${strippedColumns.join(", ")}`}
                      </div>
                    ) : null}

                    {activeTab === "uploader" ? (
                      <>
                    <div className="filter-option mt-4">
                      <div className="input-group">
                        <span className="input-group-text">
                          <div className="option-title">Set contact status: </div>
                        </span>
                        <select
                          className="form-control rmono"
                          value={status || defaultStatus || ""}
                          onChange={handleUploadStatusChange}
                        >
                          {uploaderStatusOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="example-text-alt">
                      For this campaign, the recommended default status is '
                      {defaultStatus}'
                    </div>
                    {listSourceNeedsSelection ? (
                      <div
                        className={`campaign-upload-source-picker mt-3${
                          uploadBlockedByMissingListSource ? " is-required" : ""
                        }`}
                      >
                        <div className="filter-option">
                          <div className="input-group">
                            <span className="input-group-text">
                              <div className="option-title">Where did this list come from?</div>
                            </span>
                            <select
                              className="form-control rmono"
                              value={manualListSource}
                              onChange={(e) => {
                                const next = e.target.value;
                                setManualListSource(next);
                                // The "Other" name lives in customSource; clear it
                                // when switching away so a known source isn't
                                // overridden by a stale label.
                                if (next !== MANUAL_LIST_SOURCE_OTHER) {
                                  setCustomSource("");
                                  setListSourceNotes("");
                                }
                              }}
                            >
                              <option value="">Select a source...</option>
                              {MANUAL_LIST_SOURCE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        {manualListSourceIsOther ? (
                          <div className="campaign-upload-comment">
                            <label className="campaign-upload-label">
                              Source name (required - written to the Source field on campaign
                              members and new contacts)
                            </label>
                            <input
                              type="text"
                              className="form-control rmono"
                              value={customSource}
                              onChange={(e) => setCustomSource(e.target.value)}
                              placeholder='e.g. "Event registration list", "LinkedIn Sales Navigator"'
                              maxLength={255}
                            />
                            <label className="campaign-upload-label mt-2">
                              Where did this list come from? (optional - helps us trace the data later)
                            </label>
                            <textarea
                              className="form-control rmono campaign-upload-comment-input"
                              value={listSourceNotes}
                              onChange={(e) => setListSourceNotes(e.target.value)}
                              placeholder="e.g. who supplied it, which tool/event it was exported from, when it was pulled"
                              rows={2}
                              maxLength={1000}
                            />
                          </div>
                        ) : null}
                        {uploadBlockedByMissingListSource ? (
                          <div className="campaign-upload-checkbox-error" role="alert">
                            <i className="fa-solid fa-triangle-exclamation mr-05" />
                            <span>{missingListSourceMessage}</span>
                          </div>
                        ) : (
                          <div className="campaign-upload-checkbox-help">
                            {manualListSourceIsOther
                              ? "Unrecognised sources rank lowest: they only fill empty contact fields and never overwrite data from Apollo, Seamless, ZoomInfo or manual edits."
                              : `Contact metadata will be stamped as "${manualListSource}". Pick "Other" if this file is not actually a ${manualListSource} export.`}
                          </div>
                        )}
                      </div>
                    ) : null}
                    <div className="campaign-upload-advanced mt-2">
                      <label className="campaign-upload-checkbox">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={skipExistingCampaignMembers}
                          onChange={(e) => setSkipExistingCampaignMembers(Boolean(e.target.checked))}
                        />
                        <span>Skip existing campaign members (match by email)</span>
                      </label>
                      <div className="campaign-upload-checkbox-help">
                        Existing campaign member emails are skipped so only new contacts are added.
                      </div>
                      <div className="mt-2">
                        <label className="campaign-upload-label">
                          Exclude members of other campaigns
                        </label>
                        <Select
                          isMulti
                          isSearchable
                          isClearable
                          options={availableExcludeCampaignOptions}
                          value={selectedExcludeCampaignOptions}
                          onChange={(options) =>
                            setExcludeCampaignOptions(
                              (Array.isArray(options) ? options : []).slice(0, MAX_EXCLUDE_CAMPAIGNS)
                            )
                          }
                          onMenuOpen={loadCampaignsForExclusion}
                          isOptionDisabled={() =>
                            selectedExcludeCampaignOptions.length >= MAX_EXCLUDE_CAMPAIGNS
                          }
                          isLoading={loadingFetchedCampaigns}
                          styles={excludeCampaignSelectStyles}
                          placeholder="Select campaign(s) to dedupe against"
                          noOptionsMessage={() =>
                            loadingFetchedCampaigns ? "Loading campaigns..." : "No campaigns found"
                          }
                        />
                      </div>
                      <div className="campaign-upload-checkbox-help">
                        Contacts who are members of the selected campaign(s), with any status, won't be
                        added.
                      </div>
                      <label className="campaign-upload-checkbox mt-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={runContactsThroughMillionVerifier}
                          onChange={(e) => setRunContactsThroughMillionVerifier(Boolean(e.target.checked))}
                        />
                        <span>Run contacts through MillionVerifier</span>
                      </label>
                      <div className="campaign-upload-checkbox-help">
                        Verifies new contact emails before upload and removes likely invalid or bounce-prone addresses.
                      </div>
                      {millionVerifierCreditCheck.status === "loading" ? (
                        <div className="campaign-upload-checkbox-help">
                          <i className="fa fa-spinner fa-spin mr-05" />
                          Checking MillionVerifier credits...
                        </div>
                      ) : null}
                      {millionVerifierCreditsUnavailable ? (
                        <div className="campaign-upload-checkbox-error" role="alert">
                          <i className="fa-solid fa-triangle-exclamation mr-05" />
                          <span>{millionVerifierCreditsErrorMessage}</span>
                          <button
                            type="button"
                            className="btn btn-xs btn-outline-danger ml-1"
                            onClick={checkMillionVerifierCredits}
                          >
                            Re-check credits
                          </button>
                        </div>
                      ) : null}
                      <label className="campaign-upload-checkbox mt-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={applySalesforceLeadFilters}
                          onChange={(e) => setApplySalesforceLeadFilters(Boolean(e.target.checked))}
                        />
                        <span>Apply Salesforce lead filters (recommended)</span>
                      </label>
                      <div className="campaign-upload-checkbox-help">
                        Leaves out contacts Salesforce says we shouldn't email: opted out, bounced or
                        unverified email, DNC flags, inactive accounts, open opportunities, or already
                        pending / in an opportunity for this campaign. They are not added to the campaign,
                        so the list is ready to send as soon as the upload finishes.
                      </div>
                      {!applySalesforceLeadFilters ? (
                        <div className="campaign-upload-warning">
                          Off: every resolved contact will be added, including opted-out, bounced and
                          DNC contacts. Only untick this if you will check eligibility yourself before
                          sending.
                        </div>
                      ) : null}
                      <div className="campaign-upload-toggle-row mt-1">
                        <button
                          type="button"
                          className="campaign-upload-text-toggle"
                          onClick={() => setShowSalesforceLeadFilterRules((prev) => !prev)}
                        >
                          {showSalesforceLeadFilterRules
                            ? "Hide filter rules"
                            : `Show filter rules (${SALESFORCE_FILTER_RULE_DEFS.length})`}
                        </button>
                      </div>
                      {showSalesforceLeadFilterRules ? (
                        <ul className="campaign-upload-sf-filter-rules">
                          {SALESFORCE_FILTER_RULE_DEFS.map((rule) => (
                            <li key={rule.key}>
                              {getSalesforceLeadFilterRuleLabel(rule, uploadTargetCampaignName)}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      <div className="campaign-upload-toggle-row mt-2">
                        <button
                          type="button"
                          className="campaign-upload-text-toggle"
                          onClick={() => setShowCommentInput((prev) => !prev)}
                        >
                          {showCommentInput
                            ? "Hide campaign member comment"
                            : "Set campaign member comment (optional)"}
                        </button>
                      </div>
                      {showCommentInput ? (
                        <div className="campaign-upload-comment">
                          <label className="campaign-upload-label">
                            Campaign member comment (applied to new campaign members)
                          </label>
                          <textarea
                            className="form-control rmono campaign-upload-comment-input"
                            value={campaignMemberComment}
                            onChange={(e) => setCampaignMemberComment(e.target.value)}
                            placeholder="Optional comment for new campaign members"
                            rows={2}
                          />
                          <label className="campaign-upload-checkbox mt-2">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={applyCommentToExistingCampaignMembers}
                              onChange={(e) =>
                                setApplyCommentToExistingCampaignMembers(Boolean(e.target.checked))
                              }
                            />
                            <span>Also apply this comment to matching existing campaign members</span>
                          </label>
                          <div className="campaign-upload-checkbox-help">
                            Off: comment is only set on newly added members. On: matching existing campaign
                            members in this upload are updated too.
                          </div>
                        </div>
                      ) : null}
                      {/* The "Other" source name above already IS the custom source. */}
                      {!manualListSourceIsOther ? (
                        <div className="campaign-upload-toggle-row mt-2">
                          <button
                            type="button"
                            className="campaign-upload-text-toggle"
                            onClick={() => setShowCustomSourceInput((prev) => !prev)}
                          >
                            {showCustomSourceInput
                              ? "Hide custom source"
                              : "Set custom source (optional)"}
                          </button>
                        </div>
                      ) : null}
                      {showCustomSourceInput && !manualListSourceIsOther ? (
                        <div className="campaign-upload-comment">
                          <label className="campaign-upload-label">
                            Custom source (applied to campaign members and new contacts)
                          </label>
                          <textarea
                            className="form-control rmono campaign-upload-comment-input"
                            value={customSource}
                            onChange={(e) => setCustomSource(e.target.value)}
                            placeholder="Optional source for campaign members and new contacts"
                            rows={2}
                          />
                          <div className="campaign-upload-checkbox-help">
                            When set, this overrides the auto-detected list source (e.g. Apollo,
                            Seamless, ZoomInfo) for uploaded campaign members and any newly created
                            contacts.
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div className="mt-4">
                      <button
                        className="upload-csv-btn btn btn-sm btn-primary"
                        onClick={uploadContactsToSalesforce}
                        disabled={
                          uploadingToSf ||
                          streaming ||
                          !dataHasExpectedColumns ||
                          uploadBlockedByMillionVerifier ||
                          uploadBlockedByMissingListSource
                        }
                        title={
                          !dataHasExpectedColumns
                            ? "CSV columns do not match expected format"
                            : uploadBlockedByMillionVerifier
                            ? millionVerifierCreditCheck.status === "loading"
                              ? "Checking MillionVerifier credits..."
                              : 'MillionVerifier credits unavailable - untick "Run contacts through MillionVerifier" to upload'
                            : uploadBlockedByMissingListSource
                            ? "Pick where this list came from before uploading"
                            : streaming
                            ? "Receiving progress..."
                            : "Upload contacts to Salesforce Campaign"
                        }
                      >
                        {uploadingToSf
                          ? "Starting..."
                          : streaming
                          ? "Uploading..."
                          : "Upload contacts to Salesforce Campaign"}
                        <i className="event-owner-add fa fa-upload ml-05" />
                      </button>
                      {!dataHasExpectedColumns && (
                        <div className="file-name sm mt-1">
                          Data not formatted correctly
                          <br />
                          CSV must have: {EXPECTED_COLUMN_TITLES.join(", ")}
                        </div>
                      )}
                    </div>
                      </>
                    ) : (
                      <>
                      <div className="campaign-modifier-panel mt-4">
                        <div className="example-text-alt mb-2">
                          Updates existing campaign members matched from this list by email.
                          New contacts are never created.
                        </div>

                        <label className="campaign-upload-checkbox">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={modifierUpdateStatus}
                            disabled={modifierDeleteMembers}
                            onChange={(e) => setModifierUpdateStatus(Boolean(e.target.checked))}
                          />
                          <span>Update status</span>
                        </label>
                        {modifierUpdateStatus && !modifierDeleteMembers ? (
                          <div className="campaign-modifier-subsection">
                            <div className="filter-option">
                              <div className="input-group">
                                <span className="input-group-text">
                                  <div className="option-title">Set status to: </div>
                                </span>
                                <select
                                  className="form-control rmono"
                                  value={modifierNewStatus || defaultStatus || ""}
                                  onChange={(e) => setModifierNewStatus(e.target.value)}
                                >
                                  {statusOptions.map((option) => (
                                    <option key={option} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            {["sent", "invited"].includes(
                              String(effectiveModifierNewStatus || "")
                                .trim()
                                .toLowerCase()
                            ) ? (
                              <div className="campaign-modifier-sent-note">
                                <i className="fa fa-circle-info mr-05" />
                                Last Sent and the Outreach Counter will be updated for affected
                                contacts (treated as a send).
                              </div>
                            ) : null}
                            <label className="campaign-upload-checkbox mt-2">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={modifierStatusConditionEnabled}
                                onChange={(e) =>
                                  setModifierStatusConditionEnabled(Boolean(e.target.checked))
                                }
                              />
                              <span>Only update members whose current status is:</span>
                            </label>
                            {modifierStatusConditionEnabled ? (
                              <div className="filter-option">
                                <div className="input-group">
                                  <span className="input-group-text">
                                    <div className="option-title">Current status: </div>
                                  </span>
                                  <select
                                    className="form-control rmono"
                                    value={modifierConditionStatus || defaultStatus || ""}
                                    onChange={(e) => setModifierConditionStatus(e.target.value)}
                                  >
                                    {statusOptions.map((option) => (
                                      <option key={option} value={option}>
                                        {option}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            ) : null}
                            <div className="campaign-upload-checkbox-help">
                              Protects manually-set statuses (e.g. "Attending", "Hold") by only
                              changing members currently set to the selected status.
                            </div>
                          </div>
                        ) : null}

                        <label className="campaign-upload-checkbox mt-3">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={modifierUpdateComment}
                            disabled={modifierDeleteMembers}
                            onChange={(e) => setModifierUpdateComment(Boolean(e.target.checked))}
                          />
                          <span>Update comment</span>
                        </label>
                        {modifierUpdateComment && !modifierDeleteMembers ? (
                          <div className="campaign-upload-comment">
                            <textarea
                              className="form-control rmono campaign-upload-comment-input"
                              value={modifierComment}
                              onChange={(e) => setModifierComment(e.target.value)}
                              placeholder="Comment to set on matched campaign members"
                              rows={2}
                            />
                          </div>
                        ) : null}

                        <label className="campaign-upload-checkbox mt-3">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={modifierUpdateSource}
                            disabled={modifierDeleteMembers}
                            onChange={(e) => setModifierUpdateSource(Boolean(e.target.checked))}
                          />
                          <span>Update source</span>
                        </label>
                        {modifierUpdateSource && !modifierDeleteMembers ? (
                          <div className="campaign-upload-comment">
                            <textarea
                              className="form-control rmono campaign-upload-comment-input"
                              value={modifierSource}
                              onChange={(e) => setModifierSource(e.target.value)}
                              placeholder="Source to set on matched campaign members"
                              rows={2}
                            />
                          </div>
                        ) : null}

                        <label className="campaign-upload-checkbox mt-3">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={modifierUpdateEmailVersion}
                            disabled={modifierDeleteMembers}
                            onChange={(e) =>
                              setModifierUpdateEmailVersion(Boolean(e.target.checked))
                            }
                          />
                          <span>Update email version</span>
                        </label>
                        {modifierUpdateEmailVersion && !modifierDeleteMembers ? (
                          <div className="campaign-upload-comment">
                            <textarea
                              className="form-control rmono campaign-upload-comment-input"
                              value={modifierEmailVersion}
                              onChange={(e) => setModifierEmailVersion(e.target.value)}
                              placeholder="Email version to set on matched campaign members"
                              rows={2}
                              maxLength={250}
                            />
                          </div>
                        ) : null}

                        <label className="campaign-upload-checkbox mt-3">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={modifierUpdateCampaignSentAs}
                            disabled={modifierDeleteMembers}
                            onChange={(e) =>
                              setModifierUpdateCampaignSentAs(Boolean(e.target.checked))
                            }
                          />
                          <span>Update campaign sent as</span>
                        </label>
                        {modifierUpdateCampaignSentAs && !modifierDeleteMembers ? (
                          <div className="campaign-upload-comment">
                            <Select
                              className="rmono campaign-sent-as-select"
                              classNamePrefix="campaign-sent-as-select"
                              styles={salesforceUserSelectStyles}
                              value={modifierCampaignSentAs}
                              onChange={setModifierCampaignSentAs}
                              options={salesforceUserOptions}
                              isSearchable
                              isClearable
                              placeholder="Select Salesforce user"
                              noOptionsMessage={() => "No Salesforce users found"}
                            />
                          </div>
                        ) : null}

                        <label className="campaign-upload-checkbox mt-3">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={modifierUpdateLastSent}
                            disabled={modifierDeleteMembers}
                            onChange={(e) =>
                              setModifierUpdateLastSent(Boolean(e.target.checked))
                            }
                          />
                          <span>Update last sent</span>
                        </label>
                        {modifierUpdateLastSent && !modifierDeleteMembers ? (
                          <div className="campaign-upload-comment">
                            <DatePicker
                              selected={etDatetimeLocalToPickerDate(modifierLastSent)}
                              onChange={(date) =>
                                setModifierLastSent(pickerDateToEtDatetimeLocal(date))
                              }
                              showTimeSelect
                              timeIntervals={1}
                              timeCaption="Time"
                              dateFormat={LAST_SENT_DATE_FORMAT}
                              className="form-control rmono campaign-upload-datetime-input"
                              wrapperClassName="campaign-upload-datepicker-wrapper"
                              placeholderText="Select last sent (ET)"
                            />
                            <div className="campaign-modifier-sent-note">
                              <i className="fa fa-circle-info mr-05" />
                              The Outreach Counter will also be updated for affected
                              contacts.
                            </div>
                          </div>
                        ) : null}

                        <label className="campaign-upload-checkbox campaign-modifier-delete mt-3">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={modifierDeleteMembers}
                            onChange={(e) =>
                              setModifierDeleteMembers(Boolean(e.target.checked))
                            }
                          />
                          <span>Delete campaign members</span>
                        </label>
                        {modifierDeleteMembers ? (
                          <div className="campaign-upload-checkbox-help campaign-modifier-delete-help">
                            Permanently removes every matched campaign member from this
                            campaign. This overrides the options above.
                          </div>
                        ) : null}
                      </div>
                      <div className="mt-4">
                        <button
                          className={`upload-csv-btn btn btn-sm ${
                            modifierDeleteMembers ? "btn-danger" : "btn-primary"
                          }`}
                          onClick={modifyContactsInSalesforce}
                          disabled={modifyingInSf || streaming || !modifierHasSelection}
                          title={
                            modifierHasSelection
                              ? streaming
                                ? "Receiving progress..."
                                : modifierDeleteMembers
                                ? "Delete matched campaign members"
                                : "Modify matched campaign members"
                              : "Enable at least one field to update"
                          }
                        >
                          {modifyingInSf
                            ? "Starting..."
                            : streaming
                            ? modifierDeleteMembers
                              ? "Deleting..."
                              : "Modifying..."
                            : modifierDeleteMembers
                            ? "Delete campaign members"
                            : "Modify campaign members"}
                          <i
                            className={`event-owner-add fa ${
                              modifierDeleteMembers ? "fa-trash" : "fa-pen-to-square"
                            } ml-05`}
                          />
                        </button>
                        {!modifierHasSelection ? (
                          <div className="file-name sm mt-1">
                            Enable status, comment, source, email version, campaign
                            sent as, and/or last sent to modify members.
                          </div>
                        ) : null}
                      </div>
                      </>
                    )}
                  </div>
                ) : null}
              </div>

              {csvData && csvData.length > 0 ? (
                <div className="mt-2">
                  {hasCompletedSuccessfully && (
                    <div className="campaign-upload-success-banner">
                      <i className="fa-solid fa-circle-check" />
                      <span>Contacts uploaded successfully to Salesforce Campaign.</span>
                    </div>
                  )}
                  {streaming && (
                    <div className="campaign-job-progress">
                      <div className="campaign-job-progress-head">
                        <span className="campaign-job-progress-spinner" />
                        <span>
                          {jobProgress?.phase === "delete"
                            ? "Deleting campaign members..."
                            : jobProgress?.phase === "update"
                            ? "Updating campaign members..."
                            : "Working..."}
                          {jobProgress?.total
                            ? ` ${addCommas(
                                jobProgress?.processed || 0
                              )} / ${addCommas(jobProgress?.total || 0)} (${
                                jobProgress?.percent || 0
                              }%)`
                            : ""}
                        </span>
                      </div>
                      {jobProgress?.total ? (
                        <div className="campaign-job-progress-track">
                          <div
                            className="campaign-job-progress-fill"
                            style={{
                              width: `${Math.min(
                                100,
                                Math.max(2, Number(jobProgress?.percent || 0))
                              )}%`,
                            }}
                          />
                        </div>
                      ) : null}
                      <div className="campaign-job-progress-note">
                        This runs on the server. You can safely close or leave
                        this page — the job will keep running. (You just won't
                        see live progress after leaving.)
                      </div>
                    </div>
                  )}
                  <div className="sse-log mt-3">
                    <div className="sse-controls">
                      <div className="sse-spacer" />
                      <button
                        type="button"
                        className="btn btn-xs btn-outline"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(logLines.join("\n"));
                            toast.success("Logs copied");
                          } catch {
                            toast.error("Copy failed");
                          }
                        }}
                      >
                        Copy logs
                      </button>
                      <button
                        type="button"
                        className="btn btn-xs btn-outline ml-1"
                        onClick={() => setLogLines([])}
                      >
                        Clear
                      </button>
                    </div>

                    <textarea
                      ref={logRef}
                      className="sse-log-textarea"
                      readOnly
                      value={logLines.join("\n")}
                      placeholder="Progress messages will appear here..."
                    />
                    <div className="sse-status">Status: {streaming ? "receiving updates..." : "idle"}</div>
                    {modifySummary ? (
                      <div className="sse-status mt-1">
                        <div>
                          <strong>
                            {modifySummary?.action === "delete"
                              ? "Delete breakdown"
                              : "Modify breakdown"}
                          </strong>
                        </div>
                        <div>
                          {`List rows: ${addCommas(
                            modifySummary?.counts?.inputRows || 0
                          )} | Unique emails: ${addCommas(
                            modifySummary?.counts?.inputEmails || 0
                          )} | Matched existing members: ${addCommas(
                            modifySummary?.counts?.matchedMembers || 0
                          )}`}
                        </div>
                        {modifySummary?.action === "delete" ? (
                          <div>
                            {`Members deleted: ${addCommas(
                              modifySummary?.counts?.deletedMembers || 0
                            )} | Delete failures: ${addCommas(
                              modifySummary?.counts?.recordsFailed || 0
                            )}`}
                          </div>
                        ) : (
                          <>
                            <div>
                              {`Members updated: ${addCommas(
                                modifySummary?.counts?.recordsUpdated || 0
                              )} | Update failures: ${addCommas(
                                modifySummary?.counts?.recordsFailed || 0
                              )}`}
                            </div>
                            <div>
                              {`Status set: ${addCommas(
                                modifySummary?.counts?.statusUpdated || 0
                              )} | Skipped by status condition: ${addCommas(
                                modifySummary?.counts?.statusSkippedByCondition || 0
                              )} | Comment set: ${addCommas(
                                modifySummary?.counts?.commentUpdated || 0
                              )} | Source set: ${addCommas(
                                modifySummary?.counts?.sourceUpdated || 0
                              )}`}
                            </div>
                            <div>
                              {`Email version set: ${addCommas(
                                modifySummary?.counts?.emailVersionUpdated || 0
                              )} | Campaign sent as set: ${addCommas(
                                modifySummary?.counts?.campaignSentAsUpdated || 0
                              )} | Last sent set: ${addCommas(
                                modifySummary?.counts?.lastSentUpdated ||
                                  modifySummary?.counts?.lastSentStamped ||
                                  0
                              )}`}
                            </div>
                            {modifySummary?.counts?.lastSentStamped ? (
                              <div>
                                {`Last Sent stamped (Outreach Counter incremented): ${addCommas(
                                  modifySummary?.counts?.lastSentStamped || 0
                                )}`}
                              </div>
                            ) : null}
                          </>
                        )}
                        {modifySummary?.statusInvalidSkipped ? (
                          <div>
                            Status updates were skipped: the chosen status is not valid for this
                            campaign.
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {uploadSummary ? (
                      <div className="sse-status mt-1">
                        <div>
                          <strong>Upload breakdown</strong>
                        </div>
                        <div>
                          {`Input rows: ${addCommas(
                            uploadSummary?.metrics?.originalInput ||
                              uploadSummary?.metrics?.input ||
                              0
                          )}`}
                        </div>
                        {(uploadSummary?.metrics?.skippedExistingCampaignMemberRows || 0) > 0 ? (
                          <div>
                            {`Skipped existing campaign member emails: ${addCommas(
                              uploadSummary?.metrics?.skippedExistingCampaignMemberRows || 0
                            )} (from original input ${addCommas(
                              uploadSummary?.metrics?.originalInput ||
                                uploadSummary?.metrics?.input ||
                                0
                            )})`}
                          </div>
                        ) : null}
                        {uploadSummary?.excludedCampaignMembers?.enabled ? (
                          <div>
                            {`Excluded campaign members: ${addCommas(
                              (uploadSummary?.excludedCampaignMembers?.emailMatchedRows || 0) +
                                (uploadSummary?.excludedCampaignMembers?.contactIdMatched || 0)
                            )} contact(s) skipped (in ${
                              (uploadSummary?.excludedCampaignMembers?.campaigns || [])
                                .map((campaign) => campaign?.name || campaign?.id)
                                .filter(Boolean)
                                .join(", ") || "excluded campaigns"
                            })`}
                          </div>
                        ) : null}
                        {(uploadSummary?.excludedCampaignMembers?.lookupFailures || 0) > 0 ? (
                          <div className="campaign-upload-warning">
                            {`Warning: ${addCommas(
                              uploadSummary?.excludedCampaignMembers?.lookupFailures || 0
                            )} contact(s) could not be checked against the excluded campaign(s) because the Salesforce lookup failed. Those contacts were NOT uploaded; re-upload the file to retry them.`}
                          </div>
                        ) : null}
                        {uploadSummary?.metrics?.millionVerifierEnabled ? (
                          <div>
                            {`MillionVerifier: checked ${addCommas(
                              uploadSummary?.metrics?.millionVerifierCandidates || 0
                            )} email(s) | cache hits ${addCommas(
                              uploadSummary?.metrics?.millionVerifierCacheHits || 0
                            )} | API checks ${addCommas(
                              uploadSummary?.metrics?.millionVerifierSubmitted || 0
                            )} | filtered out ${addCommas(
                              uploadSummary?.metrics?.millionVerifierFilteredOut || 0
                            )} likely bad/bounce emails | allowed ${addCommas(
                              uploadSummary?.metrics?.millionVerifierPassed || 0
                            )}`}
                          </div>
                        ) : null}
                        {(uploadSummary?.metrics?.millionVerifierFailedRequests || 0) > 0 ? (
                          <div className="campaign-upload-warning">
                            {`Warning: ${addCommas(
                              uploadSummary?.metrics?.millionVerifierFailedRequests || 0
                            )} MillionVerifier lookup(s) failed (API error, timeout, or no credits). Those contacts were NOT uploaded. Check MillionVerifier credits, then re-upload, or untick "Run contacts through MillionVerifier".`}
                          </div>
                        ) : null}
                        {uploadSummary?.salesforceLeadFilters?.enabled ? (
                          <div className="campaign-upload-sf-filter-summary">
                            <div>
                              {`Salesforce lead filters: checked ${addCommas(
                                uploadSummary?.salesforceLeadFilters?.checked || 0
                              )} contact(s) | filtered out ${addCommas(
                                uploadSummary?.salesforceLeadFilters?.filteredOut || 0
                              )} (not added to the campaign)`}
                            </div>
                            {(uploadSummary?.salesforceLeadFilters?.lookupFailures || 0) > 0 ? (
                              <div className="campaign-upload-warning">
                                {`Warning: ${addCommas(
                                  uploadSummary?.salesforceLeadFilters?.lookupFailures || 0
                                )} contact(s) could not be checked against the Salesforce lead filters (lookup failed) and were added without filtering.`}
                              </div>
                            ) : null}
                            {Array.isArray(uploadSummary?.salesforceLeadFilters?.rules) &&
                            uploadSummary.salesforceLeadFilters.rules.length ? (
                              <div className="campaign-upload-sf-filter-rows">
                                {uploadSummary.salesforceLeadFilters.rules.map((rule) => (
                                  <div
                                    key={rule.key}
                                    className={`campaign-upload-sf-filter-row${
                                      (rule.caughtCount || 0) > 0 ? " has-caught" : ""
                                    }`}
                                  >
                                    <span className="campaign-upload-sf-filter-row-label">
                                      {rule.label ||
                                        getSalesforceLeadFilterRuleLabelByKey(
                                          rule.key,
                                          uploadSummary?.salesforceLeadFilters?.campaignName ||
                                            uploadTargetCampaignName
                                        )}
                                    </span>
                                    <strong className="campaign-upload-sf-filter-row-count">
                                      {`${addCommas(rule.caughtCount || 0)} caught`}
                                    </strong>
                                  </div>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <div>Salesforce lead filters: off (no eligibility filtering applied)</div>
                        )}
                        {uploadSummary?.metrics?.contactFieldUpsertEnabled === false ? (
                          <div className="campaign-upload-warning">
                            {`Warning: the list source could not be determined for this upload, so Last Updated Source/Date metadata was NOT written to Salesforce${
                              (uploadSummary?.counts?.createdContacts || 0) > 0
                                ? ` for the ${addCommas(
                                    uploadSummary?.counts?.createdContacts || 0
                                  )} contact(s) created`
                                : ""
                            }, and existing contact fields were only filled where empty. Next time, pick a source when prompted (or set a custom source) so provenance is recorded.`}
                          </div>
                        ) : uploadSummary?.metrics?.detectedListSource ? (
                          <div>
                            {`Contact metadata source: ${uploadSummary.metrics.detectedListSource}${
                              uploadSummary?.metrics?.listSourceResolution === "custom-fallback"
                                ? " (from the custom source you entered)"
                                : ""
                            } | fields updated by source tier: ${addCommas(
                              uploadSummary?.metrics?.contactFieldsUpdatedByTier || 0
                            )                            } | empty fields filled: ${addCommas(
                              uploadSummary?.metrics?.contactFieldsFilledWhenEmpty || 0
                            )} | blank stamps reinforced (value matched, source/date recorded): ${addCommas(
                              uploadSummary?.metrics?.contactFieldsReinforced || 0
                            )} | kept (existing source outranked upload): ${addCommas(
                              uploadSummary?.metrics?.contactFieldsSkippedByLowerTier || 0
                            )}`}
                          </div>
                        ) : null}
                        {(uploadSummary?.metrics?.rowsFilteredMissingEmail || 0) +
                          (uploadSummary?.metrics?.directContactIdsWithoutEmail || 0) >
                        0 ? (
                          <div>
                            {`Skipped for missing email: ${addCommas(
                              (uploadSummary?.metrics?.rowsFilteredMissingEmail || 0) +
                                (uploadSummary?.metrics?.directContactIdsWithoutEmail || 0)
                            )} contact(s) had no valid email and were not uploaded`}
                          </div>
                        ) : null}
                        {(uploadSummary?.metrics?.rowsFilteredMissingName || 0) > 0 ? (
                          <div>
                            {`Skipped for missing name: ${addCommas(
                              uploadSummary?.metrics?.rowsFilteredMissingName || 0
                            )} contact(s) had no usable first/last name and were not uploaded`}
                          </div>
                        ) : null}
                        {(uploadSummary?.metrics?.collapsedDuplicateRows || 0) > 0 ? (
                          <div>
                            {`Duplicate rows merged: ${addCommas(
                              uploadSummary?.metrics?.collapsedDuplicateRows || 0
                            )} duplicate upload row(s) collapsed onto a single contact (no duplicate contacts created)`}
                          </div>
                        ) : null}
                        <div>
                          {`Resolved rows: ${addCommas(uploadSummary?.metrics?.resolvedRows || 0)} | Unique resolved contacts: ${addCommas(uploadSummary?.metrics?.resolvedUniqueIds || 0)}${
                            (uploadSummary?.metrics?.salesforceLeadFiltersFilteredOut || 0) > 0
                              ? ` eligible (${addCommas(
                                  uploadSummary?.metrics?.resolvedContactIdsBeforeLeadFilters || 0
                                )} before Salesforce lead filters)`
                              : ""
                          }`}
                        </div>
                        <div>{`Rows processed after filters: ${addCommas(uploadSummary?.metrics?.input || 0)}`}</div>
                        <div>
                          {`Linked campaign members: ${addCommas(uploadSummary?.counts?.linkedMembers || 0)} | Attached this run: ${addCommas(uploadSummary?.counts?.attachedMembers || 0)} | Updated campaign members this run: ${addCommas(uploadSummary?.counts?.updatedMembers || 0)}`}
                        </div>
                        <div>
                          {`Contact field backfill candidates: ${addCommas(
                            uploadSummary?.metrics?.missingContactFieldCandidates || 0
                          )} | Contact fields updated: ${addCommas(
                            uploadSummary?.metrics?.missingContactFieldUpdated || 0
                          )} | Contact field update failures: ${addCommas(
                            uploadSummary?.metrics?.missingContactFieldFailed || 0
                          )}`}
                        </div>
                        <div>
                          {`Created contacts: ${addCommas(uploadSummary?.counts?.createdContacts || 0)} | Failed creates: ${addCommas(uploadSummary?.counts?.failedCreateContacts || 0)} | Unresolved contacts: ${addCommas(uploadSummary?.counts?.unresolvedContacts || 0)}`}
                        </div>
                      </div>
                    ) : null}
                    {hasCompletedSuccessfully ? (
                      <div className="sse-status mt-2">
                        <div>
                          <strong>Upload reconciliation CSV</strong>
                        </div>
                        <div>
                          Includes every row from your uploaded CSV plus status and error reason based on Salesforce
                          campaign members after upload.
                        </div>
                        {buildingReconciliation ? (
                          <div className="mt-1">
                            <i className="fa fa-spinner fa-spin mr-05" />
                            Building reconciliation report...
                          </div>
                        ) : null}
                        <button
                          type="button"
                          className="btn btn-xs btn-outline mt-1"
                          onClick={downloadReconciliationCsv}
                          disabled={
                            buildingReconciliation || !Array.isArray(reconciliationRows) || !reconciliationRows.length
                          }
                          title={
                            buildingReconciliation
                              ? "Building reconciliation report..."
                              : "Download upload reconciliation CSV"
                          }
                        >
                          {buildingReconciliation
                            ? "Building reconciliation..."
                            : `Download reconciliation CSV (${addCommas(reconciliationRows.length)} rows)`}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CampaignContactUploader;
